// src/views/accueil/FeuilleOperateur.jsx — LA FEUILLE QUE LE FIL OUVRE.
//
// « En prod tout ouvre un écran, chez moi ça ouvre une feuille. » Voici la
// feuille. Elle ne compose rien : elle donne la description au composeur
// (`src/lib/operateur-feuille.js`, miroir du serveur) et RANGE ce qu'il rend
// dans les neuf blocs — dans l'ordre des quatre feuilles abouties de la
// maquette (feuilleCaution, feuillePaiement, feuilleTrou, feuilleRetouches) :
// le fait en tête, la frise de ce qui part tout seul, ce qu'il a regardé à
// côté, son jugement (avis · gestes · pari · base · angle mort), et les portes
// vers les écrans EN DERNIER, en second rang.
//
// ⚠️ UNE DESCRIPTION REFUSÉE PAR LE COMPOSEUR N'AFFICHE PAS UNE FEUILLE À
// MOITIÉ : elle affiche le refus, nommé. Une feuille sans son fait, sans son
// automate déclaré ou sans sa base ressemblerait à une feuille complète — et
// c'est exactement l'échec silencieux et plausible qu'on refuse.
//
// ⚠️ LES BOUTONS QUI AGISSENT OUVRENT LA CARTE QUI EXISTE DÉJÀ
// (`CarteGesteDemande`) : titre du registre, arguments en toutes lettres,
// avertissements avant le bouton, exécution par `executerGeste` avec le jeton
// de Dimitri. On ne réinvente pas une machinerie parce que l'objet change.

import { useMemo, useRef, useState } from "react";
import { DS } from "../../lib/ui";
import { composerFeuille } from "../../lib/operateur-feuille";
import { PALETTE_OP } from "../../lib/evenements-dessin";
import Feuille from "./Feuille";
import CarteGesteDemande from "../operateur/CarteGesteDemande";

const P = PALETTE_OP;
const ENCRE = "#1b2733";

/** « dim. 14 sept., 09:00 » — ou « 14 sept. » pour une date seule. Paris. */
export function quandCourt(iso) {
  if (typeof iso !== "string" || !iso) return "";
  const dateSeule = iso.length === 10;
  const d = new Date(dateSeule ? `${iso}T12:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      timeZone: "Europe/Paris",
      day: "numeric",
      month: "short",
      ...(dateSeule
        ? {}
        : { weekday: "short", hour: "2-digit", minute: "2-digit" }),
    }).format(d);
  } catch {
    return iso;
  }
}

function Inter({ children }) {
  return (
    <p
      style={{
        margin: "14px 0 7px",
        fontSize: 10.5,
        letterSpacing: ".09em",
        textTransform: "uppercase",
        color: P.gris,
        fontWeight: 600,
      }}
    >
      {children}
    </p>
  );
}

function Pastille({ enfants, fond, couleur, taille = 20 }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: taille,
        height: taille,
        borderRadius: "50%",
        background: fond,
        color: couleur,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 10,
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {enfants}
    </span>
  );
}

/** Un bouton de geste de la maquette (numéro rond · libellé · sous-titre · →). */
function BoutonGeste({ n, libelle, sous, fleche, onClic, testId, gris }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClic}
      disabled={gris}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        width: "100%",
        textAlign: "left",
        background: gris ? "rgba(255,255,255,.5)" : "#fff",
        border: gris ? `1px dashed #c3d5e4` : "none",
        borderRadius: 11,
        padding: "10px 12px",
        minHeight: 50,
        marginTop: 7,
        cursor: gris ? "default" : "pointer",
        fontFamily: DS.fontBody,
        color: ENCRE,
      }}
    >
      <Pastille
        fond={gris ? "#fff" : P.cielP}
        couleur={gris ? P.grisD : P.navy}
        enfants={gris ? "◌" : n}
      />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          style={{
            display: "block",
            fontSize: 12.5,
            fontWeight: 600,
            color: gris ? P.grisD : ENCRE,
          }}
        >
          {libelle}
        </span>
        {sous ? (
          <span
            style={{
              display: "block",
              fontSize: 10.5,
              color: gris ? P.grisD : P.gris,
              marginTop: 2,
              lineHeight: 1.45,
            }}
          >
            {sous}
          </span>
        ) : null}
      </span>
      {!gris ? (
        <span aria-hidden="true" style={{ color: P.grisD, fontSize: 13 }}>
          {fleche}
        </span>
      ) : null}
    </button>
  );
}

export default function FeuilleOperateur({
  description,
  ton,
  onFermer,
  onAller,
  onFait,
}) {
  // La composition est PURE et coûteuse (cinq règles, le registre des leviers,
  // la carte des écrans) : elle ne se refait qu'au changement de description,
  // pas à chaque frappe qui re-rend le bloc au-dessus.
  const f = useMemo(() => composerFeuille(description), [description]);
  // Le geste dont la carte est dépliée (par RANG — un seul à la fois ; deux
  // gestes peuvent porter le même libellé, et ils s'ouvraient alors ensemble).
  const [ouvert, setOuvert] = useState(null);
  const urgent = ton === "urgent";

  // ⚠️ OUVRIR UNE FEUILLE N'EST PAS LA TRAITER (2026-09-21). Le fil retire la
  // ligne dès l'ouverture : si Dimitri referme sans rien faire, l'événement
  // disparaissait de la journée alors que RIEN n'avait été fait. La feuille
  // dit donc à son hôte, en se refermant, si un geste en est parti — à lui de
  // remettre la ligne dans le fil quand il n'y en a pas eu.
  const gesteFait = useRef(false);
  const fermer = () => onFermer && onFermer({ gesteFait: gesteFait.current });

  if (!f.ok) {
    return (
      <Feuille
        testId="feuille-operateur"
        titre={f.titre || "Une feuille"}
        onFermer={fermer}
        enfants={
          <div data-testid="feuille-operateur-refus">
            <p
              style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: ENCRE }}
            >
              Je n'ai pas pu composer cette feuille : sa description est
              incomplète, et je préfère ne rien afficher plutôt qu'une feuille à
              moitié vraie.
            </p>
            <ul
              style={{
                margin: "8px 0 0",
                paddingLeft: 17,
                fontSize: 12,
                color: P.gris,
              }}
            >
              {f.defauts.map((d, i) => (
                <li key={`${i}-${d}`}>{d}</li>
              ))}
            </ul>
          </div>
        }
      />
    );
  }

  const { fait, enjeu, systeme, regarde, avis, gestes, pari, base, aveugle } =
    f;
  const jugement =
    avis ||
    gestes.agit.length ||
    gestes.attend.length ||
    pari ||
    base ||
    aveugle;

  return (
    <Feuille
      testId="feuille-operateur"
      titre={f.titre}
      onFermer={fermer}
      enfants={
        <div
          data-testid="feuille-operateur-corps"
          style={{ fontFamily: DS.fontBody, color: ENCRE }}
        >
          {/* 1 · LE FAIT — chiffré, sourcé, en tête ; 2 · L'ENJEU dessous */}
          <div
            data-testid="feuille-fait"
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 11,
              background: urgent ? P.terraBg : P.cielP,
              borderRadius: 13,
              padding: "12px 13px",
              boxShadow: `inset 0 0 0 1px ${urgent ? P.terraBd : "#c3d5e4"}`,
            }}
          >
            {fait.chiffre ? (
              <span
                aria-hidden="true"
                style={{
                  minWidth: 32,
                  height: 32,
                  padding: "0 8px",
                  borderRadius: 10,
                  background: "#fff",
                  color: urgent ? P.terraTx : P.navy,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 14,
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {fait.chiffre.valeur}
              </span>
            ) : null}
            <span style={{ flex: 1, minWidth: 0 }}>
              <b
                style={{
                  display: "block",
                  fontSize: 12.5,
                  color: urgent ? P.terraTx : P.navyD,
                  lineHeight: 1.5,
                }}
              >
                {fait.texte}
              </b>
              {enjeu ? (
                <span
                  data-testid="feuille-enjeu"
                  style={{
                    display: "block",
                    fontSize: 11,
                    marginTop: 3,
                    color: urgent ? P.terraTx : P.navy,
                    opacity: 0.9,
                    lineHeight: 1.5,
                  }}
                >
                  {[enjeu.cout, enjeu.bloque].filter(Boolean).join(" · ")}
                </span>
              ) : null}
              <span
                data-testid="feuille-source"
                style={{
                  display: "block",
                  fontSize: 10,
                  marginTop: 4,
                  color: P.gris,
                }}
              >
                d'après {fait.source}
              </span>
            </span>
          </div>

          {/* 3 · CE QUE LE SYSTÈME FAIT DÉJÀ SEUL — daté. C'est lui qui décide. */}
          {systeme ? (
            <div data-testid="feuille-systeme">
              <Inter>Ce qui est prévu</Inter>
              {systeme.automate ? (
                <p
                  style={{
                    margin: "0 0 8px",
                    fontSize: 12,
                    lineHeight: 1.55,
                    color: P.gris,
                  }}
                >
                  {systeme.automate.phrase}
                  {systeme.automate.reserve
                    ? ` ${systeme.automate.reserve}`
                    : ""}
                </p>
              ) : null}
              {systeme.prevu.length || systeme.rendezVous ? (
                <div style={{ position: "relative", paddingLeft: 15 }}>
                  <span
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      left: 5,
                      top: 9,
                      bottom: 9,
                      width: 1.5,
                      background: P.bord,
                    }}
                  />
                  {[
                    ...systeme.prevu,
                    ...(systeme.rendezVous
                      ? [{ ...systeme.rendezVous, rendezVous: true }]
                      : []),
                  ].map((m, i) => (
                    <div
                      key={`${m.quand}-${i}`}
                      data-testid={
                        m.rendezVous ? "feuille-rendez-vous" : undefined
                      }
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 11,
                        padding: "6px 0",
                        position: "relative",
                      }}
                    >
                      <span
                        aria-hidden="true"
                        style={{
                          position: "absolute",
                          left: -14,
                          top: 11,
                          width: 9,
                          height: 9,
                          borderRadius: "50%",
                          background: m.bloque
                            ? P.terra
                            : m.rendezVous
                              ? P.navy
                              : "#fff",
                          boxShadow: `0 0 0 2px ${m.bloque ? P.terraBg : P.bord}`,
                        }}
                      />
                      <span
                        style={{
                          width: 92,
                          flexShrink: 0,
                          fontSize: 10,
                          color: P.grisD,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {quandCourt(m.quand)}
                      </span>
                      <span
                        style={{
                          flex: 1,
                          fontSize: 12,
                          fontWeight: m.bloque || m.rendezVous ? 600 : 500,
                          color: m.bloque ? P.terraTx : ENCRE,
                          lineHeight: 1.45,
                        }}
                      >
                        {m.quoi}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          {/* 4 · CE QU'IL A REGARDÉ À CÔTÉ — « ! » sur ce qui cloche, « ✕ » sur ce qu'il élimine */}
          {regarde.length ? (
            <div data-testid="feuille-regarde">
              <Inter>Ce que j'ai vérifié à côté</Inter>
              <div
                style={{
                  background: "#fff",
                  border: `1px solid ${P.bord}`,
                  borderRadius: 12,
                }}
              >
                {regarde.map((r, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 10,
                      padding: "9px 12px",
                      borderTop: i ? `1px solid ${P.bord}` : "none",
                    }}
                  >
                    <Pastille
                      taille={18}
                      fond={r.cloche ? P.terraBg : P.fond}
                      couleur={r.cloche ? P.terraTx : P.grisD}
                      enfants={r.cloche ? "!" : r.elimine ? "✕" : "·"}
                    />
                    <span
                      style={{
                        fontSize: 12,
                        lineHeight: 1.5,
                        color: r.cloche
                          ? P.terraTx
                          : r.elimine
                            ? P.gris
                            : ENCRE,
                        fontWeight: r.cloche ? 600 : 400,
                      }}
                    >
                      {r.texte}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* 5→9 · SON JUGEMENT : avis, gestes, pari, base, angle mort */}
          {jugement ? (
            <div
              data-testid="feuille-jugement"
              style={{
                marginTop: 14,
                background: P.cielP,
                borderRadius: 13,
                padding: "12px 14px",
                boxShadow: "inset 0 0 0 1px #c3d5e4",
              }}
            >
              {avis ? (
                <div
                  data-testid="feuille-avis"
                  style={{
                    background: "#fff",
                    borderRadius: 11,
                    padding: "11px 13px",
                    marginBottom: 11,
                    boxShadow: `inset 0 0 0 1.5px ${P.navyD}`,
                  }}
                >
                  <span
                    style={{
                      display: "block",
                      fontSize: 10,
                      letterSpacing: ".06em",
                      textTransform: "uppercase",
                      fontWeight: 600,
                      color: P.navyD,
                      marginBottom: 6,
                    }}
                  >
                    À votre place
                  </span>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 12.5,
                      lineHeight: 1.65,
                      color: P.navyD,
                    }}
                  >
                    {avis}
                  </p>
                </div>
              ) : null}

              {/* CE QUE JE PEUX FAIRE — chaque bouton ouvre la carte, qui exécute */}
              {gestes.agit.map((g, i) => (
                <div
                  key={`${g.libelle}-${i}`}
                  data-testid={`feuille-geste-${i}`}
                >
                  <BoutonGeste
                    n={i + 1}
                    libelle={g.libelle}
                    sous={
                      g.sous ||
                      (g.type === "lot"
                        ? `${g.gestes.length} dates, chacune à son prix`
                        : g.constatable)
                    }
                    fleche="→"
                    testId={`feuille-geste-bouton-${i}`}
                    onClic={() => setOuvert(ouvert === i ? null : i)}
                  />
                  {ouvert === i ? (
                    <div
                      data-testid={`feuille-geste-carte-${i}`}
                      style={{ marginTop: 7 }}
                    >
                      {(g.type === "lot" ? g.gestes : [g]).map((c, j) => (
                        <CarteGesteDemande
                          key={`${c.levier}-${j}`}
                          geste={c}
                          onFait={(geste, r) => {
                            gesteFait.current = true;
                            if (onFait) onFait(geste, r);
                          }}
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}

              {/* CE QUI ATTEND — gris, motivé, daté quand c'est un rendez-vous */}
              {gestes.attend.map((g, i) => (
                <BoutonGeste
                  key={`${g.libelle}-${i}`}
                  gris
                  libelle={g.libelle}
                  sous={[
                    g.quand ? `${quandCourt(g.quand)} —` : "",
                    g.motif,
                    // Le gris d'un geste estampillé porte souvent un « sous »
                    // (« elle passe dans Fait ») : il se perdait ici.
                    g.sous,
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  testId={`feuille-attend-${i}`}
                />
              ))}

              {/* LE PARI — chiffré, daté, avec son seuil d'erreur */}
              {pari ? (
                <div
                  data-testid="feuille-pari"
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    background: "#fff",
                    border: "1px dashed #87b8e6",
                    borderRadius: 12,
                    padding: "11px 13px",
                    marginTop: 11,
                  }}
                >
                  <Pastille fond={P.cielP} couleur={P.navy} enfants="◈" />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span
                      style={{
                        display: "block",
                        fontSize: 10,
                        letterSpacing: ".06em",
                        textTransform: "uppercase",
                        fontWeight: 600,
                        color: P.navy,
                        marginBottom: 4,
                      }}
                    >
                      Ce que je parie
                    </span>
                    <span
                      style={{
                        display: "block",
                        fontSize: 12.5,
                        lineHeight: 1.6,
                        color: P.navyD,
                      }}
                    >
                      {pari.texte} — {pari.seuil_erreur}.
                    </span>
                    <span
                      style={{
                        display: "block",
                        fontSize: 10.5,
                        color: P.gris,
                        marginTop: 4,
                      }}
                    >
                      Je reviens vous le dire le {quandCourt(pari.verif_le)}.
                    </span>
                  </span>
                </div>
              ) : null}

              {/* LA BASE — sur combien de données, et si c'est peu */}
              {base ? (
                <p
                  data-testid="feuille-base"
                  style={{
                    margin: "11px 0 0",
                    fontSize: 11,
                    lineHeight: 1.55,
                    color: P.gris,
                  }}
                >
                  Je me base sur {base.phrase}
                  {/[.!?…]$/.test(base.phrase) ? "" : "."}
                </p>
              ) : null}

              {/* L'ANGLE MORT — ce qu'il ne voit pas, ou ce qui manque, nommé */}
              {aveugle ? (
                <p
                  data-testid="feuille-aveugle"
                  style={{
                    margin: "8px 0 0",
                    fontSize: 11,
                    lineHeight: 1.55,
                    color: P.gris,
                  }}
                >
                  Ce que je ne vois pas : {aveugle}
                  {/[.!?…]$/.test(aveugle) ? "" : "."}
                </p>
              ) : null}
            </div>
          ) : null}

          {/* LA NAVIGATION — en second rang, en dernier */}
          <div
            style={{
              display: "flex",
              gap: 7,
              justifyContent: "flex-end",
              marginTop: 13,
              flexWrap: "wrap",
            }}
          >
            {gestes.navigue
              .filter((n) => n.ecran || onAller)
              .map((n, i) => (
                <button
                  key={`${n.libelle}-${i}`}
                  type="button"
                  data-testid={`feuille-navigue-${i}`}
                  onClick={() =>
                    onAller &&
                    onAller(
                      n.ecran
                        ? { ecran: n.ecran, cible: n.cible }
                        : { vue: n.vue, args: n.args },
                    )
                  }
                  style={{
                    minHeight: 38,
                    padding: "0 13px",
                    borderRadius: 9,
                    background: P.cielP,
                    border: "1px solid #87b8e6",
                    color: P.navyD,
                    fontSize: 11.5,
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: DS.fontBody,
                  }}
                >
                  {n.libelle} ›
                </button>
              ))}
            <button
              type="button"
              data-testid="feuille-operateur-ecarter"
              onClick={fermer}
              style={{
                minHeight: 38,
                padding: "0 11px",
                fontSize: 11.5,
                color: P.gris,
                background: "none",
                border: "none",
                cursor: "pointer",
                fontFamily: DS.fontBody,
              }}
            >
              Fermer
            </button>
          </div>
        </div>
      }
    />
  );
}
